import dayjs from "dayjs";
import { renderToString } from "react-dom/server";

import { dayAndShortMonth, yAxisLabelsConfig } from "@similarweb/pro-logic-components";
import type { ColumnDefinition } from "@similarweb/ui-components";
import { ChartTooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { percentageSignFilter } from "app/filters/ngFilters";
import { getTooltipTitle } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/overview-report/brands-visibility/utils";
import type { DatesRange } from "app/pages/sales-intelligence/sub-modules/account-overview/shared/types/common";
import type { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import combineConfigs from "pro-features/components/Chart/src/combineConfigs";
import dailyIntervalConfig from "pro-features/components/Chart/src/configs/granularity/dailyIntervalConfig";
import bothAxisZoomConfig from "pro-features/components/Chart/src/configs/zoom/bothAxisZoomConfig";

import type { LineGraphDataRowType, SeriesLineItem, TooltipColumnDefinition } from "./types";

export const getLineGraphConfig = (
    data: Array<SeriesLineItem>,
    granularity: GranularityNames,
    reportDuration: DatesRange,
) => {
    const yAxisFormatter = ({ value }) => percentageSignFilter()(value, 0);
    return combineConfigs({ yAxisFormatter }, [
        dailyIntervalConfig,
        yAxisLabelsConfig,
        bothAxisZoomConfig,
        {
            chart: {
                height: 300,
                marginRight: 16,
                spacing: [0, 0, 0, 0],
            },
            plotOptions: {
                line: {
                    connectNulls: true,
                },
                series: {
                    connectNulls: true,
                },
            },
            yAxis: {
                gridLineWidth: 0.5,
                showFirstLabel: true,
                tickLength: 20,
                startOnTick: true,
                minPadding: 0,
                min: 0,
                max: null,
            },
            xAxis: {
                gridLineWidth: 0,
                minPadding: 0,
                maxPadding: 0,
                labels: {
                    formatter: function () {
                        return dayjs.utc(this.value).format(dayAndShortMonth);
                    },
                },
            },
            tooltip: {
                shared: true,
                outside: true,
                useHTML: true,
                formatter() {
                    const rows = data
                        .filter((item) => item.visible)
                        .map((rawData) => {
                            const point = rawData.data.find((p) => p[0] === this.x);

                            return {
                                displayName: rawData.name,
                                fieldValue: percentageSignFilter()(point?.[1], 0),
                                originalValue: point?.[1],
                                id: rawData.id,
                                isChecked: rawData.visible,
                                items: {
                                    label: rawData.name,
                                    color: rawData.color,
                                },
                            };
                        })
                        .sort((a, b) => b.originalValue - a.originalValue);

                    const columnDefinition: ColumnDefinition<TooltipColumnDefinition>[] = [
                        {
                            key: "items",
                            title: translate("llm.brands-visibility.graph.legend.header.brands"),
                            selector: "items" as const,
                            align: "left",
                            size: "150px",
                            render: ({ items }: LineGraphDataRowType) => (
                                <ChartTooltip.LabelCell
                                    label={items.label}
                                    backgroundColor={items.color}
                                />
                            ),
                        },
                        {
                            key: "fieldValue",
                            title: translate("llm.brands-visibility.graph.tooltip.title"),
                            selector: "fieldValue" as const,
                            align: "right",
                            size: "50px",
                            render: ({ fieldValue }: LineGraphDataRowType) => (
                                <ChartTooltip.ValueCell value={fieldValue.toString()} />
                            ),
                        },
                    ];

                    const tooltipTitle = getTooltipTitle(dayjs.utc(this.key), granularity, reportDuration);
                    return renderToString(
                        <Flex width="220px">
                            <ChartTooltip
                                width={255}
                                title={tooltipTitle}
                                columnDefinition={columnDefinition}
                                // @ts-expect-error FIXME: Types of property 'fieldValue' are incompatible.
                                data={rows}
                            />
                        </Flex>,
                    );
                },
            },
        },
    ]);
};
